import React from 'react';
import { Box, makeStyles } from '@material-ui/core';
import { Popup } from 'react-map-gl';
import { useTranslate } from 'react-admin';
import { AvatarDetail } from '../../types';
import { AvatarPopupField } from './AvatarPopupField';
import { toFormatDate, toFormatUtcDate } from '../../../../utils/date';
import { getNameWithoutPrefix } from '../../../../utils/resources/MetadataAvatarType';
import ShowLastNoteIcon1 from '../../../showLastNoteIcon/showLastNoteIcon1';
import { MAINTENANCE } from '../../../../utils/rikutec';
import './AvatarPopup.css';

interface AvatarPopupProps {
    detail: AvatarDetail | null;
    loading: boolean;
    error: string | null;
    longitude: number;
    latitude: number;
    onClose: () => void;
    feature?: any;
}

const useStyles = makeStyles({
    loadingContainer: {
        padding: '16px',
        textAlign: 'center',
    },
    spinner: {
        width: '20px',
        height: '20px',
        border: '2px solid #f3f3f3',
        borderTop: '2px solid #007bff',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto 8px',
    },
    error: {
        color: '#b00020',
        padding: '16px',
    },
    detailContainer: {
        maxWidth: '400px',
        padding: '20px',
        fontFamily: 'Roboto, sans-serif',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        '@media (max-width:600px)': {
            maxWidth: 'calc(100vw - 32px)',
            padding: '12px',
        },
    },
    header: {
        borderBottom: 0,
        paddingBottom: '16px',
        marginBottom: '20px',
        background: '#e5f0fa',
        margin: '-20px -20px 20px -20px',
        padding: '20px 20px 16px 20px',
        borderRadius: '12px 12px 0 0',
    },
    headerTitle: {
        fontSize: '18px',
        fontWeight: 700,
        color: '#092b4b',
        marginBottom: '6px',
        letterSpacing: '0.5px',
    },
    headerSubtitle: {
        fontSize: '15px',
        color: '#424242',
        fontWeight: 500,
        lineHeight: '1.4',
    },
    mainGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '12px 16px',
        fontSize: '13px',
        '@media (max-width:600px)': {
            gridTemplateColumns: '1fr',
        },
    },
    serialNumberLabel: {
        fontWeight: 600,
        color: '#092b4b',
        marginBottom: '4px',
        fontSize: '11px',
        letterSpacing: '0.8px',
        backgroundColor: '#e3f2fd',
        padding: '2px 6px',
        borderRadius: '4px',
        display: 'inline-block',
    },
    serialNumberValue: {
        color: '#333',
        fontWeight: 500,
        fontSize: '14px',
        padding: '4px 0',
    },
    section: {
        marginTop: '16px',
        paddingTop: '16px',
        borderTop: '1px solid #e0e0e0',
    },
    sectionTitle: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#555',
        marginBottom: '12px',
        letterSpacing: '0.5px',
    },
    technicalGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '8px 16px',
        fontSize: '13px',
        '@media (max-width:600px)': {
            gridTemplateColumns: '1fr',
        },
    },
    technicalLabel: {
        fontWeight: 500,
        color: '#666',
        fontSize: '12px',
    },
    technicalValue: {
        color: '#333',
    },
    infoBlock: {
        marginBottom: '8px',
    },
    infoLabel: {
        fontWeight: 600,
        color: '#555',
        fontSize: '12px',
        letterSpacing: '0.5px',
        marginBottom: '4px',
    },
    infoValue: {
        color: '#333',
        fontSize: '13px',
        lineHeight: '1.4',
    },
});

export const AvatarPopup: React.FC<AvatarPopupProps> = ({
    detail,
    loading,
    error,
    longitude,
    latitude,
    onClose,
    feature,
}) => {
    const classes = useStyles();
    const t = useTranslate();

    // Helper function to parse JSON strings if needed
    const parseIfString = (value: any) => {
        if (typeof value === 'string') {
            try {
                return JSON.parse(value);
            } catch (error) {
                console.error('Error parsing JSON string:', error);
                return null;
            }
        }
        return value;
    };

    // Merge detail with feature properties to get both original data and new maintenance/vidange info
    const displayData = {
        ...detail,
        ...(feature?.properties || {}),
        // Ensure we have the next maintenance and vidange from feature properties
        nextMaintenance: parseIfString(feature?.properties?.nextMaintenance) || null,
        nextVidange: parseIfString(feature?.properties?.nextVidange) || null,
        metabaseMetadata: feature?.properties?.metabaseMetadata || [],
    };
    
    return (
        <Popup
            longitude={longitude}
            latitude={latitude}
            anchor="top"
            onClose={onClose}
            closeOnClick={false}
            closeButton={true}
            maxWidth="420px"
            className="avatar-popup"
        >
            {loading && (
                <Box className={classes.loadingContainer}>
                    <Box className={classes.spinner} />
                    <Box>{t('app.common.loading') || 'Loading details...'}</Box>
                </Box>
            )}
            {error && !loading && (
                <Box className={classes.error}>Error: {error}</Box>
            )}
            {displayData && !loading && !error && (
                <div
                    style={{
                        maxWidth: '400px',
                        padding: '20px',
                        fontFamily: 'Roboto, sans-serif',
                        backgroundColor: '#ffffff',
                        borderRadius: '12px',
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            borderBottom: 0,
                            paddingBottom: '16px',
                            marginBottom: '20px',
                            background: '#e5f0fa',
                            margin: '-20px -20px 20px -20px',
                            padding: '20px 20px 16px 20px',
                            borderRadius: '12px 12px 0 0',
                        }}
                    >
                        <div
                            style={{
                                fontSize: '18px',
                                fontWeight: 700,
                                color: '#092b4b',
                                marginBottom: '6px',
                                letterSpacing: '0.5px',
                            }}
                        >
                            {displayData.productTypology}
                        </div>
                        <div
                            style={{
                                fontSize: '15px',
                                color: '#424242',
                                fontWeight: 500,
                                lineHeight: '1.4',
                            }}
                        >
                            {getNameWithoutPrefix(displayData.productTypeName || '')}
                        </div>
                    </div>

                    {/* Main Information Grid */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px 16px',
                            fontSize: '13px',
                        }}
                    >
                        {/* Serial Number */}
                        <div>
                            <div
                                style={{
                                    fontWeight: 600,
                                    color: '#092b4b',
                                    marginBottom: '4px',
                                    fontSize: '11px',
                                    letterSpacing: '0.8px',
                                    backgroundColor: '#e3f2fd',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                    display: 'inline-block',
                                }}
                            >
                                {t('app.ancs.serialNumber')}
                            </div>
                            <div
                                style={{
                                    color: '#333',
                                    fontWeight: 500,
                                    fontSize: '14px',
                                    padding: '4px 0',
                                }}
                            >
                                {displayData.productSerialNumber || '—'}
                            </div>
                        </div>

                        <AvatarPopupField label={t('app.ancs.postalCode')} value={displayData.productPostalCode} />

                        <AvatarPopupField
                            label={t('app.ancs.installingDate')}
                            value={displayData.productInstallationDate ? toFormatDate(displayData.productInstallationDate) : undefined}
                        />

                        <AvatarPopupField
                            label={t('app.ancs.nodeCurrentState')}
                            value={displayData.productCurrentStatusName}
                            valueStyle={{ fontWeight: 500 }}
                        />

                        <AvatarPopupField
                            label={t('app.ancs.maintenanceLastDate')}
                            value={displayData.productLastMaintenanceDate ? toFormatUtcDate(displayData.productLastMaintenanceDate) : undefined}
                            icon={
                                displayData.productLastMaintenanceData ? (
                                    <ShowLastNoteIcon1 record={displayData.productLastMaintenanceData} tag={MAINTENANCE} />
                                ) : undefined
                            }
                        />

                        <AvatarPopupField
                            label={t('app.ancs.sludgeEmptyingLastDate')}
                            value={displayData.productLastEmptyingDate ? toFormatUtcDate(displayData.productLastEmptyingDate) : undefined}
                            icon={
                                displayData.productLastEmptyingData ? (
                                    <ShowLastNoteIcon1 record={displayData.productLastEmptyingData} tag={MAINTENANCE} />
                                ) : undefined
                            }
                        />

                        {/* Next Maintenance */}
                        {(() => {
                            let maintenanceValue = undefined;
                            if (displayData.nextMaintenance?.execution_date) {
                                try {
                                    maintenanceValue = toFormatDate(displayData.nextMaintenance.execution_date);
                                } catch (error) {
                                    console.error('Error formatting maintenance date:', error);
                                    // Fallback to simple date formatting
                                    maintenanceValue = new Date(displayData.nextMaintenance.execution_date).toLocaleDateString('fr-FR');
                                }
                            }
                            return (
                                <AvatarPopupField
                                    label="Prochaine maintenance"
                                    value={maintenanceValue}
                                    valueStyle={{ fontWeight: 500 }}
                                />
                            );
                        })()}

                        {/* Next Vidange */}
                        {(() => {
                            let vidangeValue = undefined;
                            if (displayData.nextVidange?.execution_date) {
                                try {
                                    vidangeValue = toFormatDate(displayData.nextVidange.execution_date);
                                } catch (error) {
                                    console.error('Error formatting vidange date:', error);
                                    // Fallback to simple date formatting
                                    vidangeValue = new Date(displayData.nextVidange.execution_date).toLocaleDateString('fr-FR');
                                }
                            }
                            return (
                                <AvatarPopupField
                                    label="Prochaine vidange"
                                    value={vidangeValue}
                                    valueStyle={{ fontWeight: 500 }}
                                />
                            );
                        })()}
                    </div>

                    {/* Technical Specifications */}
                    {(displayData.processingCapacity ||
                        displayData.sludgeStorageVolume ||
                        displayData.totalUsefulVolume ||
                        displayData.model) && (
                        <div
                            style={{
                                marginTop: '16px',
                                paddingTop: '16px',
                                borderTop: '1px solid #e0e0e0',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '14px',
                                    fontWeight: 600,
                                    color: '#555',
                                    marginBottom: '12px',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                {t('app.ancs.actions.title') + ' - Technical'}
                            </div>
                            <div
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '8px 16px',
                                    fontSize: '13px',
                                }}
                            >
                                {displayData.processingCapacity && (
                                    <div>
                                        <div
                                            style={{
                                                fontWeight: 500,
                                                color: '#666',
                                                fontSize: '12px',
                                            }}
                                        >
                                            {t('app.ancs.processingCapacity')}
                                        </div>
                                        <div style={{ color: '#333' }}>{displayData.processingCapacity}</div>
                                    </div>
                                )}
                                {displayData.sludgeStorageVolume && (
                                    <div>
                                        <div
                                            style={{
                                                fontWeight: 500,
                                                color: '#666',
                                                fontSize: '12px',
                                            }}
                                        >
                                            {t('app.ancs.sludgeStorageVolume')}
                                        </div>
                                        <div style={{ color: '#333' }}>{displayData.sludgeStorageVolume}</div>
                                    </div>
                                )}
                                {displayData.totalUsefulVolume && (
                                    <div>
                                        <div
                                            style={{
                                                fontWeight: 500,
                                                color: '#666',
                                                fontSize: '12px',
                                            }}
                                        >
                                            {t('app.ancs.totalUsefulVolume')}
                                        </div>
                                        <div style={{ color: '#333' }}>{displayData.totalUsefulVolume}</div>
                                    </div>
                                )}
                                {displayData.model && (
                                    <div>
                                        <div
                                            style={{
                                                fontWeight: 500,
                                                color: '#666',
                                                fontSize: '12px',
                                            }}
                                        >
                                            {t('app.ancs.model')}
                                        </div>
                                        <div style={{ color: '#333' }}>{displayData.model}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Address and Description */}
                    {(displayData.addresstext || displayData.description || displayData.comment) && (
                        <div
                            style={{
                                marginTop: '16px',
                                paddingTop: '16px',
                                borderTop: '1px solid #e0e0e0',
                            }}
                        >
                            {displayData.addresstext && (
                                <div style={{ marginBottom: '8px' }}>
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            color: '#555',
                                            fontSize: '12px',
                                            letterSpacing: '0.5px',
                                            marginBottom: '4px',
                                        }}
                                    >
                                        {t('app.ancs.name') + ' - Address'}
                                    </div>
                                    <div
                                        style={{
                                            color: '#333',
                                            fontSize: '13px',
                                            lineHeight: '1.4',
                                        }}
                                    >
                                        {displayData.addresstext}
                                    </div>
                                </div>
                            )}
                            {displayData.description && (
                                <div style={{ marginBottom: '8px' }}>
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            color: '#555',
                                            fontSize: '12px',
                                            letterSpacing: '0.5px',
                                            marginBottom: '4px',
                                        }}
                                    >
                                        {t('app.ancs.name') + ' - Description'}
                                    </div>
                                    <div
                                        style={{
                                            color: '#333',
                                            fontSize: '13px',
                                            lineHeight: '1.4',
                                        }}
                                    >
                                        {displayData.description}
                                    </div>
                                </div>
                            )}
                            {displayData.comment && (
                                <div>
                                    <div
                                        style={{
                                            fontWeight: 600,
                                            color: '#555',
                                            fontSize: '12px',
                                            letterSpacing: '0.5px',
                                            marginBottom: '4px',
                                        }}
                                    >
                                        {t('app.ancs.name') + ' - Comment'}
                                    </div>
                                    <div
                                        style={{
                                            color: '#333',
                                            fontSize: '13px',
                                            lineHeight: '1.4',
                                        }}
                                    >
                                        {displayData.comment}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </Popup>
    );
};
